<template>
  <div class="auth-container" :style="containerStyle">
    <div class="auth-box card">
      <div class="login-logo" v-if="brandingLogo && !logoError">
        <img :src="brandingLogo" :alt="organizationName" @error="handleLogoError" />
      </div>
      <div v-else-if="logoError" class="login-logo-text">
        <h1>{{ organizationName }}</h1>
      </div>
      <h2 class="text-center">Reset Your Password</h2>
      
      <div v-if="!submitted">
        <p class="description">
          Enter your email address and we'll send you a link to reset your password.
        </p>
        
        <form @submit.prevent="handleSubmit">
          <div class="form-group">
            <label for="email">Email Address</label>
            <input 
              type="email" 
              id="email" 
              v-model="email" 
              class="form-control" 
              required 
              autocomplete="email"
              placeholder="your@email.com"
            >
          </div>
          
          <div v-if="error" class="error-message">{{ error }}</div>
          
          <button type="submit" class="btn btn-block" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            {{ loading ? 'Sending...' : 'Send Reset Link' }}
          </button>
        </form>
      </div>
      
      <div v-else class="success-state">
        <div class="success-icon">
          <i class="fas fa-envelope-open-text"></i>
        </div>
        <h3>Check Your Email</h3>
        <p>
          If an account exists for <strong>{{ email }}</strong>, you will receive a password reset link shortly.
        </p>
        <p class="note">
          Didn't receive the email? Check your spam folder or try again.
        </p>
        <button @click="resetForm" class="btn btn-secondary">
          Try Another Email
        </button>
      </div>
      
      <div class="back-link">
        <router-link :to="loginRoute">
          <i class="fas fa-arrow-left"></i> Back to Login
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import whitelabelBranding from '@/services/whitelabelBrandingService';
import { resolveOrgSlugFromHost } from '@/utils/associationPortal';

export default {
  name: 'ForgotPassword',
  data() {
    return {
      email: '',
      loading: false,
      error: '',
      submitted: false,
      organizationSlug: null,
      branding: null,
      logoError: false,
    };
  },

  async mounted() {
    this.organizationSlug = this.$route.query.org || resolveOrgSlugFromHost() || null;
    if (this.organizationSlug) {
      try {
        await whitelabelBranding.loadOrganizationBranding(this.organizationSlug);
        this.branding = whitelabelBranding.getCurrentBranding();
      } catch (error) {
        // Continue with default branding
      }
    }
  },

  computed: {
    brandingLogo() {
      return whitelabelBranding.getLogo();
    },
    organizationName() {
      return whitelabelBranding.getAppName();
    },
    containerStyle() {
      const primaryColor = whitelabelBranding.getColor('primary');
      return { '--login-primary': primaryColor };
    },
    loginRoute() {
      if (this.organizationSlug) {
        return `/login?org=${this.organizationSlug}`;
      }
      return '/login';
    },
  },

  methods: {
    handleLogoError() {
      this.logoError = true;
    },
    async handleSubmit() {
      this.loading = true;
      this.error = '';
      
      try {
        await api.post('/users/password-reset/', {
          email: this.email.toLowerCase().trim()
        });
        
        this.submitted = true;
      } catch (error) {
        // Always show success to prevent email enumeration
        // The backend will handle the actual logic
        this.submitted = true;
      } finally {
        this.loading = false;
      }
    },
    
    resetForm() {
      this.email = '';
      this.submitted = false;
      this.error = '';
    }
  }
};
</script>

<style scoped>
.auth-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: var(--color-surface);
}

.auth-box {
  width: 100%;
  max-width: 400px;
}

.text-center {
  text-align: center;
}

.description {
  color: #6b7280;
  text-align: center;
  margin-bottom: 2rem;
  line-height: 1.6;
}

label {
  display: block;
  margin-bottom: var(--spacing-sm);
  font-weight: 600;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-control {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #e1e5e9;
  border-radius: var(--border-radius);
  font-size: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-control:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
}

.btn {
  width: 100%;
  padding: 0.875rem 1rem;
  border: none;
  border-radius: var(--border-radius);
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.login-logo {
  text-align: center;
  margin-bottom: 1.5rem;
}

.login-logo img {
  max-width: 220px;
  max-height: 70px;
  object-fit: contain;
}

.login-logo-text {
  text-align: center;
  margin-bottom: 1.5rem;
}

.login-logo-text h1 {
  font-size: 1.4rem;
  font-weight: 600;
  color: var(--login-primary, var(--color-primary, #1E40AF));
  margin: 0;
}

.btn-block {
  background-color: var(--login-primary, var(--color-primary, #1E40AF));
  color: white;
}

.btn-block:hover:not(:disabled) {
  background-color: var(--login-primary, var(--color-primary, #1E40AF));
  filter: brightness(110%);
}

.btn-block:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #f3f4f6;
  color: #374151;
  margin-top: 1rem;
}

.btn-secondary:hover {
  background-color: #e5e7eb;
}

.error-message {
  color: #dc2626;
  background: #fef2f2;
  padding: 0.75rem;
  border-radius: var(--border-radius);
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

.success-state {
  text-align: center;
  padding: 1rem 0;
}

.success-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}

.success-icon i {
  font-size: 2rem;
  color: white;
}

.success-state h3 {
  color: #111827;
  margin-bottom: 1rem;
}

.success-state p {
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 0.5rem;
}

.success-state .note {
  font-size: 0.875rem;
  color: #9ca3af;
}

.back-link {
  margin-top: 2rem;
  text-align: center;
  padding-top: 1.5rem;
  border-top: 1px solid #e1e5e9;
}

.back-link a {
  color: #6b7280;
  text-decoration: none;
  font-size: 0.875rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: color 0.2s ease;
}

.back-link a:hover {
  color: var(--color-primary, #1E40AF);
}

.spinner-border {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spinner-border 0.75s linear infinite;
  margin-right: 0.5rem;
}

@keyframes spinner-border {
  to {
    transform: rotate(360deg);
  }
}
</style>
